import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { DialogContent, Button, Grid, Typography } from '@material-ui/core';

// UTILITY
import setYupValidationSchema from '@/utility/validationSchema/validationSchema';

// HOC
import withStripeElements from '@/hoc/withStripeElements';

// COMPONENTS
import Loader from '@/components/Loader';
import StripeCardFields from '@/components/Payment/StripeCardFields';

// STYLES
import { DialogStyled, CloseIcon } from './styles';

const DialogChangeCard = ({
  initialValues = {},
  defaultConfirmState = false,
  stripe,
  elements,
  open,
  onClose,
  onSubmit,
  loading,
}) => {
  const [t] = useTranslation();
  const [confirm, setConfirm] = useState(defaultConfirmState);

  const handleConfirm = () => setConfirm(!confirm);

  const handleSubmit = values => {
    if (!stripe || !elements) {
      return;
    }

    const card = elements.getElement('cardNumber');
    const callback = () => setConfirm(false);

    onSubmit(stripe, card, values.zipcode, callback);
  };

  const handleDialogClose = (e, reason) => {
    if (reason === 'backdropClick' && loading) return;

    onClose();
  };

  return (
    <DialogStyled
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={handleDialogClose}
      TransitionProps={{ onExited: () => setConfirm(defaultConfirmState) }}
    >
      <DialogContent>
        <CloseIcon size="medium" aria-label="close" onClick={onClose}>
          <i className="icon-close" />
        </CloseIcon>

        <div>
          <Typography variant="h3" align="center">
            {t('profileDetails.creditCard.modalTitle')}
          </Typography>
          <Typography className="m-t-10" variant="body1" align="center">
            {confirm
              ? t('profileDetails.creditCard.setupDesc')
              : t('profileDetails.creditCard.confirmationDesc')}
          </Typography>
        </div>

        {confirm && (
          <div className="m-t-30">
            <Formik
              initialValues={initialValues}
              validationSchema={setYupValidationSchema('stripePaymentSchema', t)}
              validateOnMount
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isValid, setFieldValue, setFieldTouched, values }) => {
                return (
                  <Form>
                    <StripeCardFields
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      values={values}
                    />
                    <Grid className="m-t-20" container spacing={1}>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          color="primary"
                          disabled={!stripe || !isValid}
                        >
                          {t('buttons.confirm')}
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Button color="secondary" fullWidth onClick={handleConfirm}>
                          {t('buttons.cancel')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          </div>
        )}

        {!confirm && (
          <Grid className="m-t-20" container spacing={1}>
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleConfirm}
              >
                {t('buttons.changeCard')}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button color="secondary" fullWidth onClick={onClose}>
                {t('buttons.close')}
              </Button>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <Loader active={loading} />
    </DialogStyled>
  );
};

DialogChangeCard.propTypes = {
  initialValues: PropTypes.object,
  defaultConfirmState: PropTypes.bool,
  stripe: PropTypes.object,
  elements: PropTypes.object,
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default compose(withStripeElements, React.memo)(DialogChangeCard);
